import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarCheck, CheckCircle2, Clock, PhoneCall } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WIDGET_SRC = "https://assets.calendly.com/assets/external/widget.js";

function Frame({ children }) {
  return (
    <div className="min-h-screen bg-[#030712] noise-overlay relative flex flex-col overflow-x-hidden" data-testid="schedule-page">
      <div className="absolute inset-0 hero-grid-bg" aria-hidden="true" />
      <header className="relative z-10 border-b border-slate-800/70 bg-[#030712]/70 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-5 py-1.5 sm:py-2 flex items-center justify-center">
          <a href="/" data-testid="schedule-home-link"><img src="/bfg-logo.png" alt="Breeze Financial Group" className="h-28 sm:h-32 -my-7 sm:-my-8 w-auto object-contain drop-shadow-[0_0_18px_rgba(0,198,239,0.4)]" /></a>
        </div>
      </header>
      <main className="relative z-10 flex-1 px-4 py-8 sm:py-12">
        <div className="mx-auto w-full max-w-3xl">{children}</div>
      </main>
    </div>
  );
}

function Heading({ eyebrow, title, text }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="text-center">
      <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-300">{eyebrow}</div>
      <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold text-white" data-testid="schedule-title">{title}</h1>
      {text && <p className="mx-auto mt-3 max-w-xl text-sm sm:text-base text-slate-400">{text}</p>}
    </motion.div>
  );
}

function fmt(iso, tz) {
  try { return new Date(iso).toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz || undefined, timeZoneName: "short" }); } catch { return iso; }
}

/** Calendly inline embed for the assigned agent's event type. Agent identity is hidden via hide_event_type_details. */
function CalendlyInline({ eventUrl, prefill, trackingId, onScheduled }) {
  const root = useRef(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const init = () => {
      if (!window.Calendly || !root.current) return;
      root.current.replaceChildren();
      window.Calendly.initInlineWidget({
        url: eventUrl, parentElement: root.current,
        prefill: { name: prefill.name, firstName: prefill.first_name, lastName: prefill.last_name, email: prefill.email, phone: prefill.phone, customAnswers: { a1: prefill.phone } },
        utm: { utmSource: "breeze-funnel", utmMedium: "application", utmCampaign: "recruiting", utmContent: trackingId },
      });
      setLoaded(true);
    };
    let script = document.querySelector(`script[src="${WIDGET_SRC}"]`);
    if (window.Calendly) init();
    else {
      if (!script) { script = document.createElement("script"); script.src = WIDGET_SRC; script.async = true; document.body.appendChild(script); }
      script.addEventListener("load", init);
    }
    const onMsg = (e) => { if (e.origin === "https://calendly.com" && e.data?.event === "calendly.event_scheduled") onScheduled(e.data?.payload); };
    window.addEventListener("message", onMsg);
    return () => { window.removeEventListener("message", onMsg); script?.removeEventListener("load", init); };
  }, [eventUrl, prefill, trackingId, onScheduled]);
  return (
    <div className="relative">
      {!loaded && <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400" data-testid="schedule-calendar-loading">Loading available times…</div>}
      <div ref={root} data-testid="schedule-calendar" className="min-h-[720px] w-full overflow-hidden rounded-2xl bg-white" />
    </div>
  );
}

export default function SchedulePage() {
  const token = new URLSearchParams(window.location.search).get("s") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [booked, setBooked] = useState(false);

  useEffect(() => {
    if (!token) { setError("This scheduling link is invalid."); return; }
    axios.get(`${API}/booking/${encodeURIComponent(token)}`).then((r) => setData(r.data)).catch(() => setError("This scheduling link is invalid or has expired."));
  }, [token]);

  const onScheduled = async () => {
    setBooked(true);
    try { await axios.post(`${API}/booking/${encodeURIComponent(token)}/scheduled`); } catch { /* webhook remains the source of truth */ }
  };

  if (error) return <Frame><Heading eyebrow="Breeze Financial Group · Recruiting Team" title="Application Received!" text="Our recruiting team has your application and will be reaching out shortly to coordinate your introductory conversation." /><p className="mt-6 text-center text-xs text-slate-500" data-testid="schedule-error">{error}</p></Frame>;
  if (!data) return <Frame><div className="py-20 text-center text-sm text-slate-400" data-testid="schedule-loading">Preparing your next step…</div></Frame>;

  if (booked || data.state === "scheduled") {
    const appt = data.appointment;
    return (
      <Frame>
        <Heading eyebrow="Breeze Financial Group · Recruiting Team" title="You're all set!" text="Your introductory interview is booked. A calendar invitation with the details is on its way to your inbox." />
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="mx-auto mt-8 max-w-md rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-6 text-center" data-testid="schedule-confirmed">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
          <p className="mt-3 text-lg font-bold text-white">Interview confirmed</p>
          {appt?.start_time && <p className="mt-1 text-sm text-emerald-200" data-testid="schedule-confirmed-time">{fmt(appt.start_time, appt.timezone)}</p>}
          <p className="mt-3 text-xs text-slate-400">Need to change it? Use the links in your confirmation e-mail.</p>
        </motion.div>
        <p className="mt-10 text-center"><a href="/" className="text-sm font-semibold text-cyan-300 hover:underline">Back to Breeze</a></p>
      </Frame>
    );
  }

  if (data.state !== "ready") {
    return (
      <Frame>
        <Heading eyebrow="Breeze Financial Group · Recruiting Team" title="Application Received!" text="Thank you for your interest in joining Breeze Financial Group." />
        <div className="mx-auto mt-8 max-w-md rounded-2xl border border-slate-700 bg-[#0b1528] p-6 text-center" data-testid="schedule-unavailable">
          <PhoneCall className="mx-auto h-9 w-9 text-cyan-300" />
          <p className="mt-3 text-sm leading-relaxed text-slate-200">{data.message}</p>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <Heading eyebrow="Breeze Financial Group · Recruiting Team" title="Application Received!" text="Thank you for your interest in joining Breeze Financial Group. The next step is to schedule a brief introductory conversation with our recruiting team." />
      <div className="mt-8 flex items-center justify-center gap-2 text-white">
        <CalendarCheck className="h-5 w-5 text-cyan-300" />
        <h2 className="text-base md:text-lg font-bold" data-testid="schedule-booking-heading">Schedule Your Introductory Interview</h2>
      </div>
      <p className="mt-1 flex items-center justify-center gap-1.5 text-xs text-slate-400"><Clock className="h-3.5 w-3.5" /> Pick any time that works for you — times shown in your local timezone.</p>
      <div className="mt-5">
        <CalendlyInline eventUrl={data.event_url} prefill={data.prefill} trackingId={data.tracking_id} onScheduled={onScheduled} />
      </div>
    </Frame>
  );
}
